import { useEffect, useState } from "react";
import { FolderKanban, Plus, X } from "lucide-react";
import { fetchProjects, createProject, type Project } from "../lib/api";
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  Button,
} from "../components/ui";

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchProjects()
      .then((data) => setProjects(Array.isArray(data) ? data : []))
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await createProject({ name: name.trim(), description: desc.trim() || undefined });
      setShowNew(false);
      setName("");
      setDesc("");
      load();
    } catch (e: any) {
      setError(e?.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Group your scans by product, client, or engagement."
      >
        <Button onClick={() => setShowNew((v) => !v)}>
          <Plus className="h-4 w-4" /> New project
        </Button>
      </PageHeader>

      {showNew && (
        <div className="card animate-rise p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-base font-semibold tracking-tight text-mist-100">
              Create project
            </h2>
            <button onClick={() => setShowNew(false)} className="text-mist-500 hover:text-mist-200">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            <input
              className="field"
              placeholder="Project name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <textarea
              className="field resize-none"
              rows={2}
              placeholder="Description (optional)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowNew(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating || !name.trim()}>
                {creating ? "Creating…" : "Create project"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading && <LoadingState label="Loading projects…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && projects.length === 0 && (
        <EmptyState
          icon={<FolderKanban className="h-6 w-6" />}
          title="No projects yet"
          description="Create a project to organize your targets and scan history."
          action={
            <Button size="sm" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4" /> Create project
            </Button>
          }
        />
      )}

      {!loading && !error && projects.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div
              key={p.id}
              className="card group p-5 transition-colors hover:border-line-strong"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-volt-400/10 text-volt-400">
                  <FolderKanban className="h-5 w-5" />
                </span>
                <h3 className="truncate font-medium text-mist-100">{p.name}</h3>
              </div>
              <p className="mb-4 line-clamp-2 min-h-[2.5rem] text-sm text-mist-400">
                {p.description || "No description"}
              </p>
              <div className="flex items-center gap-4 border-t border-line pt-3 text-xs text-mist-500">
                <span>{p.scans_count ?? 0} scans</span>
                <span className="text-mist-600">·</span>
                <span>{p.findings_count ?? 0} findings</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
